/**
 * @file UpdateNotification.tsx
 * @description Toast notification cuando hay nueva versión de la app disponible
 * Permite al usuario actualizar manualmente sin interrumpir su navegación
 * @author XiaVan Team
 */

import { useState, useEffect } from 'react'
import { RefreshCw, X } from 'lucide-react'

export default function UpdateNotification() {
  const [isVisible, setIsVisible] = useState(false)
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)

  useEffect(() => {
    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg)
        
        // Check for updates every 60 seconds
        setInterval(() => {
          reg.update()
        }, 60000)
      })

      // Listen for updates
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        setIsVisible(true)
      })
    }
  }, [])

  const handleUpdate = () => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      window.location.reload()
    }
    setIsVisible(false)
  }

  const handleDismiss = () => {
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[90] sm:left-auto sm:w-96">
      <div className="rounded-lg bg-blue-600 p-4 shadow-2xl dark:bg-blue-500">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="rounded-full bg-white/20 p-2">
            <RefreshCw className="h-5 w-5 text-white" />
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="font-semibold text-white">
              Nueva versión disponible
            </h3>
            <p className="mt-1 text-sm text-blue-100">
              Hay una actualización lista para instalar
            </p>

            {/* Actions */}
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleUpdate}
                className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50"
              >
                Actualizar ahora
              </button>
              <button
                onClick={handleDismiss}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
              >
                Más tarde
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="rounded-full p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
